import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export type Tier = "free" | "software" | "cloud_starter" | "cloud_pro" | "cloud_empire";

export interface TierLimits {
  tier: Tier;
  label: string;
  taskCap: number | null;       // null = unlimited
  ventureCap: number | null;
  journalDays: number | null;
  aiDailyCap: number | null;
  cloudStorageMb: number | null; // null = unlimited, 0 = no cloud
}

const TIER_RANK: Record<Tier, number> = {
  free: 0, software: 1, cloud_starter: 2, cloud_pro: 3, cloud_empire: 4,
};

const TIER_TABLE: Record<Tier, TierLimits> = {
  free:          { tier: "free",          label: "Free",          taskCap: 0,     ventureCap: 0,     journalDays: 0,    aiDailyCap: 0,    cloudStorageMb: 0 },
  software:      { tier: "software",      label: "Software (Lifetime)", taskCap: 100,   ventureCap: 3,     journalDays: 30,   aiDailyCap: 3,    cloudStorageMb: 0 },
  cloud_starter: { tier: "cloud_starter", label: "Cloud Starter", taskCap: 2000,  ventureCap: 20,    journalDays: 365,  aiDailyCap: 20,   cloudStorageMb: 100 },
  cloud_pro:     { tier: "cloud_pro",     label: "Cloud Pro",     taskCap: 10000, ventureCap: 50,    journalDays: null, aiDailyCap: 100,  cloudStorageMb: 1024 },
  cloud_empire:  { tier: "cloud_empire",  label: "Cloud Empire",  taskCap: null,  ventureCap: null,  journalDays: null, aiDailyCap: null, cloudStorageMb: 10240 },
};

export function tierLimitsFor(tier: Tier): TierLimits { return TIER_TABLE[tier]; }

const CLOUD_PLAN_TO_TIER: Record<string, Tier> = {
  cloud_starter_monthly: "cloud_starter",
  cloud_starter_yearly: "cloud_starter",
  cloud_pro_monthly: "cloud_pro",
  cloud_pro_yearly: "cloud_pro",
  cloud_empire_monthly: "cloud_empire",
  cloud_empire_yearly: "cloud_empire",
};

interface SubscriptionInfo {
  planSlug: string | null;
  status: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

interface AuthCtx {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  hasPurchased: boolean;          // owns the one-time software
  hasAccess: boolean;             // can use the app at all (tier !== free)
  hasCloudAccess: boolean;        // cloud sync unlocked
  hasCompGrant: boolean;
  onboardingCompleted: boolean;
  tier: Tier;
  limits: TierLimits;
  subscription: SubscriptionInfo | null;
  signOut: () => Promise<void>;
  refreshFlags: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasPurchased, setHasPurchased] = useState(false);
  const [hasCompGrant, setHasCompGrant] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [tier, setTier] = useState<Tier>("free");
  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);

  const loadFlags = async (uid: string) => {
    const [{ data: roles }, { data: profile }, { data: onb }, { data: grants }, { data: subs }] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", uid),
      supabase.from("profiles").select("has_purchased").eq("user_id", uid).maybeSingle(),
      supabase.from("onboarding_profiles").select("completed").eq("user_id", uid).maybeSingle(),
      supabase.from("comp_grants").select("expires_at,active,plan_slug").eq("user_id", uid).eq("active", true),
      supabase.from("subscriptions").select("plan_slug,status,current_period_end,cancel_at_period_end,created_at")
        .eq("user_id", uid).order("created_at", { ascending: false }),
    ]);

    const admin = !!roles?.some((r: any) => r.role === "admin");
    const purchased = !!profile?.has_purchased;
    const now = Date.now();
    const activeGrant = (grants || []).find((g: any) => !g.expires_at || new Date(g.expires_at).getTime() > now);
    const activeSub = (subs || []).find((s: any) => {
      const endOk = !s.current_period_end || new Date(s.current_period_end).getTime() > now;
      return endOk && ["active", "trialing", "past_due", "canceled"].includes(s.status);
    });

    setIsAdmin(admin);
    setHasPurchased(purchased);
    setOnboardingCompleted(!!onb?.completed);
    setHasCompGrant(!!activeGrant);
    setSubscription(activeSub ? {
      planSlug: activeSub.plan_slug,
      status: activeSub.status,
      currentPeriodEnd: activeSub.current_period_end,
      cancelAtPeriodEnd: !!activeSub.cancel_at_period_end,
    } : null);

    // Derive tier
    let derived: Tier = "free";
    if (admin) derived = "cloud_empire";
    else if (activeSub && activeSub.plan_slug && CLOUD_PLAN_TO_TIER[activeSub.plan_slug]) {
      derived = CLOUD_PLAN_TO_TIER[activeSub.plan_slug];
    } else if (activeGrant?.plan_slug && CLOUD_PLAN_TO_TIER[activeGrant.plan_slug as string]) {
      derived = CLOUD_PLAN_TO_TIER[activeGrant.plan_slug as string];
    } else if (activeGrant) {
      derived = "cloud_starter";
    } else if (purchased) {
      derived = "software";
    }
    setTier(derived);
  };

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      setUser(sess?.user ?? null);
      if (sess?.user) setTimeout(() => loadFlags(sess.user.id), 0);
      else {
        setIsAdmin(false); setHasPurchased(false); setOnboardingCompleted(false);
        setHasCompGrant(false); setTier("free"); setSubscription(null);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) loadFlags(session.user.id).finally(() => setLoading(false));
      else setLoading(false);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(`auth-${user.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "subscriptions", filter: `user_id=eq.${user.id}` },
        () => loadFlags(user.id))
      .on("postgres_changes", { event: "*", schema: "public", table: "profiles", filter: `user_id=eq.${user.id}` },
        () => loadFlags(user.id))
      .on("postgres_changes", { event: "*", schema: "public", table: "comp_grants", filter: `user_id=eq.${user.id}` },
        () => loadFlags(user.id))
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);

  // Re-evaluate access on focus / visibility so a canceled sub flips to "free"
  // the moment its period ends, even if the tab has been open the whole time.
  useEffect(() => {
    if (!user) return;
    const refresh = () => loadFlags(user.id);
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [user?.id]);

  // Schedule a refresh at the exact moment the current period ends, so a
  // canceled subscription loses access the instant it expires.
  useEffect(() => {
    if (!user) return;
    const end = subscription?.currentPeriodEnd;
    if (!end) return;
    const ms = new Date(end).getTime() - Date.now();
    if (ms <= 0 || ms > 2_147_000_000) return;
    const t = setTimeout(() => loadFlags(user.id), ms + 1000);
    return () => clearTimeout(t);
  }, [user?.id, subscription?.currentPeriodEnd]);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Hard redirect to landing so all in-memory state resets and protected
    // pages don't linger after the session is gone.
    window.location.assign("/");
  };
  const refreshFlags = async () => { if (user) await loadFlags(user.id); };

  const hasAccess = TIER_RANK[tier] > 0;
  const hasCloudAccess = TIER_RANK[tier] >= 2;
  const limits = TIER_TABLE[tier];

  return (
    <Ctx.Provider value={{ session, user, loading, isAdmin, hasPurchased, hasAccess, hasCloudAccess, hasCompGrant, onboardingCompleted, tier, limits, subscription, signOut, refreshFlags }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
